import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import {
  User,
  Mail,
  Lock,
  Eye,
  EyeOff,
  CheckCircle,
  AlertCircle,
  ArrowRight,
  ArrowLeft,
  UserPlus,
  LogIn,
  Phone,
  Users,
  Award,
  Briefcase,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import { authService } from '../../services/auth.service';
import { useAuth } from '../../context/AuthContext';
import EmailVerificationInput from '../../components/auth/EmailVerificationInput';
import ForgotPasswordLink from '../../components/auth/ForgotPasswordLink';
import { useTranslation } from 'react-i18next';
import logo from '../../assets/logoblanc.png';

type AuthMode = 'login' | 'register';

interface FormData {
  email: string;
  password: string;
  confirmPassword?: string;
  companyName?: string;
  phone?: string;
  consentToDataProcessing?: boolean;
  rememberMe?: boolean;
}

const ConsultantAuthPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login, logout } = useAuth();
  const [authMode, setAuthMode] = useState<AuthMode>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    email: '',
    password: '',
    confirmPassword: '',
    companyName: '',
    phone: '',
    consentToDataProcessing: false,
    rememberMe: false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [successMessage, setSuccessMessage] = useState('');
  const [emailVerificationError, setEmailVerificationError] = useState<{
    email: string;
    userType: number;
  } | null>(null);
  const [isConsentExpanded, setIsConsentExpanded] = useState(false);

  // Check URL parameters on component mount
  useEffect(() => {
    const mode = searchParams.get('mode');
    if (mode === 'register') {
      setAuthMode('register');
    }
  }, [searchParams]);

  const handleInputChange = (field: keyof FormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value as any }));
    // Clear error when user starts typing
    if (errors[field as string]) {
      setErrors(prev => {
        const next = { ...prev };
        delete next[field as string];
        return next;
      });
    }
    // Clear email verification error when user changes email
    if (field === 'email') {
      setEmailVerificationError(null);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Email validation
    if (!formData.email) {
      newErrors.email = 'L\'email est requis';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Format d\'email invalide';
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Le mot de passe est requis';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Le mot de passe doit contenir au moins 8 caractères';
    }

    // Register-specific validations
    if (authMode === 'register') {
      if (!formData.confirmPassword) {
        newErrors.confirmPassword = 'Confirmez votre mot de passe';
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Les mots de passe ne correspondent pas';
      }

      if (!formData.companyName) newErrors.companyName = 'Le nom de l\'entreprise est requis';
      if (!formData.phone) newErrors.phone = 'Le numéro de téléphone est requis';
      if (!formData.consentToDataProcessing) newErrors.consentToDataProcessing = t('auth.validation.consentRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);

    try {
      if (authMode === 'login') {
        // Try login - restricted to consultants and admins only
        const result = await login(formData.email, formData.password, !!formData.rememberMe);

        // Check if the user has the correct role for this form
        const isAdmin = result.roleId === 1 || result.roleName === 'admin';
        const isConsultant = result.roleId === 4 || result.roleName === 'consultant';

        if (!isAdmin && !isConsultant) {
          // User is neither admin nor consultant - logout and show error
          logout();
          setEmailVerificationError(null);
          if (result.roleId === 2 || result.roleName === 'training_center') {
            setErrors({ email: 'Ce compte est réservé aux consultants. Veuillez utiliser le formulaire de connexion approprié.' });
          } else {
            setErrors({ email: 'Ce compte ne peut pas se connecter à partir de ce formulaire. Veuillez utiliser le formulaire de connexion approprié.' });
          }
          setIsLoading(false);
          return;
        }

        setSuccessMessage(t('auth.success.redirecting'));

        // Redirect based on actual user role
        setTimeout(() => {
          if (result.roleId === 1) {
            navigate('/admin/dashboard');
          } else if (result.roleId === 4) {
            navigate('/consultant/dashboard');
          } else {
            navigate('/dashboard');
          }
        }, 1500);
      } else {
        // Handle registration - Step 1: Basic info only
        const registrationData = {
          email: formData.email,
          password: formData.password,
          role_id: 4 as const, // consultant role ID
          centerName: formData.companyName,
          phone: formData.phone
        };

        const response = await authService.register(registrationData);

        if (response.success) {
          // Show success message
          setSuccessMessage(t('auth.success.accountCreated'));

          // Redirect to email verification after showing success message
          setTimeout(() => {
            navigate(`/auth/verify-email?email=${encodeURIComponent(formData.email)}&role=consultant`);
          }, 2000);
        }
      }
    } catch (error: any) {
      // Handle specific error messages
      let errorMessage = t('common.error');
      if (error.message.includes('existe déjà')) {
        errorMessage = t('auth.consultant.validation.emailExists');
      } else if (error.message.includes('Invalid credentials')) {
        errorMessage = t('auth.validation.emailInvalid');
      } else if (error.message.includes('Internal server error')) {
        errorMessage = t('auth.consultant.validation.serverError');
      } else {
        errorMessage = error.message;
      }

      // Check if this is an email verification error
      if (error.message.includes('Veuillez vérifier votre email') ||
        error.message.includes('EMAIL_NOT_VERIFIED') ||
        error.response?.data?.code === 'EMAIL_NOT_VERIFIED') {
        setEmailVerificationError({
          email: error.response?.data?.email || formData.email,
          userType: error.response?.data?.user_type || 4 // consultant role
        });
        setErrors({});
      } else {
        setEmailVerificationError(null);
        setErrors({ email: errorMessage });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const toggleAuthMode = () => {
    setAuthMode(prev => prev === 'login' ? 'register' : 'login');
    setErrors({});
    setSuccessMessage('');
    setEmailVerificationError(null);
  };

  return (
    <div className="min-h-screen bg-white flex">
      {/* Left Panel - Branding & Info */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        {/* Full-width background image */}
        <div
          className="absolute inset-0 bg-gradient-to-br from-purple-600 via-pink-500 to-teal-400"
        />

        {/* Overlay for better text readability */}
        <div className="absolute inset-0 bg-black/20" />

        <div className="relative z-10 flex flex-col justify-between h-full p-6 sm:p-8 lg:p-12 text-white">
          <div>
            <div className="flex items-center mb-6 sm:mb-8">
              <img src={logo} alt="PHINLEX" className="h-16 sm:h-20 lg:h-24 w-auto" />
            </div>

            <div className="max-w-md">
              <h1 className="text-lg sm:text-xl lg:text-2xl font-bold mb-3 sm:mb-4 leading-tight">
                {t('auth.consultant.title')}
              </h1>

              <div className="space-y-3 mb-6">
                <div className="flex items-start gap-2">
                  <div className="w-4 h-4 bg-brand-mint rounded-full flex items-center justify-center mt-1 flex-shrink-0">
                    <CheckCircle className="h-3 w-3 text-brand-primary" />
                  </div>
                  <p className="text-sm text-gray-100 leading-relaxed">
                    {t('auth.consultant.benefits.specialized')}
                  </p>
                </div>

                <div className="flex items-start gap-2">
                  <div className="w-4 h-4 bg-brand-mint rounded-full flex items-center justify-center mt-1 flex-shrink-0">
                    <CheckCircle className="h-3 w-3 text-brand-primary" />
                  </div>
                  <p className="text-sm text-gray-100 leading-relaxed">
                    {t('auth.consultant.benefits.audience')}
                  </p>
                </div>

                <div className="flex items-start gap-2">
                  <div className="w-4 h-4 bg-brand-mint rounded-full flex items-center justify-center mt-1 flex-shrink-0">
                    <CheckCircle className="h-3 w-3 text-brand-primary" />
                  </div>
                  <p className="text-sm text-gray-100 leading-relaxed">
                    {t('auth.consultant.benefits.collaborations')}
                  </p>
                </div>

                <div className="flex items-start gap-2">
                  <div className="w-4 h-4 bg-brand-mint rounded-full flex items-center justify-center mt-1 flex-shrink-0">
                    <CheckCircle className="h-3 w-3 text-brand-primary" />
                  </div>
                  <p className="text-sm text-gray-100 leading-relaxed">
                    {t('auth.consultant.benefits.credibility')}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-brand-mint/20 rounded-lg flex items-center justify-center">
                  <User className="h-4 w-4 text-brand-mint" />
                </div>
                <div>
                  <div className="font-semibold text-sm">{t('auth.consultant.features.profile')}</div>
                  <div className="text-xs text-gray-300">{t('auth.consultant.features.profileDesc')}</div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-brand-accent/20 rounded-lg flex items-center justify-center">
                  <Briefcase className="h-4 w-4 text-brand-accent" />
                </div>
                <div>
                  <div className="font-semibold text-sm">{t('auth.consultant.features.services')}</div>
                  <div className="text-xs text-gray-300">{t('auth.consultant.features.servicesDesc')}</div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-brand-secondary/20 rounded-lg flex items-center justify-center">
                  <Award className="h-4 w-4 text-brand-secondary" />
                </div>
                <div>
                  <div className="font-semibold text-sm">{t('auth.consultant.features.recognition')}</div>
                  <div className="text-xs text-gray-300">{t('auth.consultant.features.recognitionDesc')}</div>
                </div>
              </div>
            </div>
          </div>


        </div>
      </div>

      {/* Right Panel - Auth Forms */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-4 sm:p-6 bg-white">
        <div className="w-full max-w-md">
          {/* Logo for mobile */}
          <div className="lg:hidden flex items-center justify-center mb-6">
            <img src={logo} alt="PHINLEX" className="h-16 w-auto" />
          </div>

          {/* Back Button */}
          <div className="mb-6">
            <Link
              to="/auth/role-selection"
              className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              {t('auth.userAuth.changeProfile')}
            </Link>
          </div>

          {/* Auth Card */}
          <div className="bg-white rounded-2xl shadow-xl border border-black/5 p-4 sm:p-6 lg:p-8">

            {/* Header */}
            <div className="text-center mb-6 sm:mb-8">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-brand-primary/10 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <User className="h-6 w-6 sm:h-8 sm:w-8 text-brand-primary" />
              </div>
              <h1 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">
                {t('auth.consultant.subtitle')}
              </h1>
              <p className="text-xs sm:text-sm text-gray-600">
                {authMode === 'login'
                  ? t('auth.consultant.form.loginButton')
                  : t('auth.consultant.form.registerButton')
                }
              </p>
            </div>

            {/* Success Message */}
            {successMessage && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3"
              >
                <CheckCircle className="h-5 w-5 text-green-600" />
                <p className="text-green-800 font-medium">{successMessage}</p>
              </motion.div>
            )}

            {/* Registration Steps Info */}
            {authMode === 'register' && (
              <div className="mb-6 p-4 bg-brand-primary/10 border border-brand-primary/20 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-6 h-6 bg-brand-primary text-white rounded-full flex items-center justify-center text-xs font-bold">1</div>
                  <span className="font-semibold text-brand-primary">{t('auth.consultant.form.step1')}</span>
                </div>
                <p className="text-sm text-brand-primary/80">
                  {t('auth.consultant.form.step1Desc')}
                </p>
              </div>
            )}

            {/* Auth Mode Tabs */}
            <div className="flex bg-gray-100 rounded-lg p-1 mb-6 sm:mb-8">
              <button
                onClick={() => setAuthMode('login')}
                className={`flex-1 py-2 sm:py-3 px-2 sm:px-4 rounded-md text-xs sm:text-sm font-semibold transition-all flex items-center justify-center gap-1 sm:gap-2 ${authMode === 'login'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
                  }`}
              >
                <LogIn className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="hidden xs:inline">{t('auth.consultant.form.signIn')}</span>
                <span className="xs:hidden">{t('auth.consultant.form.signIn')}</span>
              </button>
              <button
                onClick={() => setAuthMode('register')}
                className={`flex-1 py-2 sm:py-3 px-2 sm:px-4 rounded-md text-xs sm:text-sm font-semibold transition-all flex items-center justify-center gap-1 sm:gap-2 ${authMode === 'register'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
                  }`}
              >
                <UserPlus className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="hidden xs:inline">{t('auth.consultant.form.createAccount')}</span>
                <span className="xs:hidden">{t('auth.consultant.form.createAccount')}</span>
              </button>
            </div>

            {/* Auth Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Email Verification Input */}
              {emailVerificationError && (
                <EmailVerificationInput
                  email={emailVerificationError.email}
                  userType={emailVerificationError.userType}
                  onVerificationSuccess={() => {
                    setEmailVerificationError(null);
                    navigate('/auth/consultant/complete-profile');
                  }}
                  onBackToLogin={() => {
                    setEmailVerificationError(null);
                  }}
                  onError={(error) => {
                    setErrors({ email: error });
                  }}
                />
              )}
              <AnimatePresence mode="wait">
                {authMode === 'register' && (
                  <motion.div
                    key="register-fields"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="space-y-4 mb-4"
                  >
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('auth.consultant.form.companyName')} *
                      </label>
                      <input
                        type="text"
                        value={formData.companyName}
                        onChange={(e) => handleInputChange('companyName', e.target.value)}
                        className={`w-full px-3 sm:px-4 py-2 sm:py-3 rounded-lg border transition-colors text-sm sm:text-base ${errors.companyName
                          ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                          : 'border-gray-300 focus:border-brand-primary focus:ring-brand-primary/20'
                          } focus:outline-none focus:ring-2`}
                        placeholder={t('auth.consultant.form.companyNamePlaceholder')}
                      />
                      {errors.companyName && (
                        <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                          <AlertCircle className="h-4 w-4" />
                          {errors.companyName}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('auth.consultant.form.phone')} *
                      </label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                        <input
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => handleInputChange('phone', e.target.value)}
                          className={`w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-3 rounded-lg border transition-colors text-sm sm:text-base ${errors.phone
                            ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                            : 'border-gray-300 focus:border-emerald-500 focus:ring-emerald-500/20'
                            } focus:outline-none focus:ring-2`}
                          placeholder={t('auth.consultant.form.phonePlaceholder')}
                        />
                      </div>
                      {errors.phone && (
                        <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                          <AlertCircle className="h-4 w-4" />
                          {errors.phone}
                        </p>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Email Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('auth.consultant.form.email')} *
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className={`w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-3 rounded-lg border transition-colors text-sm sm:text-base ${errors.email
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                      : 'border-gray-300 focus:border-brand-primary focus:ring-brand-primary/20'
                      } focus:outline-none focus:ring-2`}
                    placeholder={t('auth.consultant.form.emailPlaceholder')}
                  />
                </div>
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="h-4 w-4" />
                    {errors.email}
                  </p>
                )}
              </div>

              {/* Password Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('auth.consultant.form.password')} *
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    className={`w-full pl-8 sm:pl-10 pr-10 sm:pr-12 py-2 sm:py-3 rounded-lg border transition-colors text-sm sm:text-base ${errors.password
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                      : 'border-gray-300 focus:border-brand-primary focus:ring-brand-primary/20'
                      } focus:outline-none focus:ring-2`}
                    placeholder={t('auth.consultant.form.passwordPlaceholder')}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="h-4 w-4" />
                    {errors.password}
                  </p>
                )}
                <ForgotPasswordLink />
              </div>

              {/* Confirm Password Field (Register only) */}
              <AnimatePresence>
                {authMode === 'register' && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                  >
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('auth.consultant.form.confirmPassword')} *
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={formData.confirmPassword}
                        onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                        className={`w-full pl-8 sm:pl-10 pr-10 sm:pr-12 py-2 sm:py-3 rounded-lg border transition-colors text-sm sm:text-base ${errors.confirmPassword
                          ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                          : 'border-gray-300 focus:border-brand-primary focus:ring-brand-primary/20'
                          } focus:outline-none focus:ring-2`}
                        placeholder={t('auth.consultant.form.confirmPasswordPlaceholder')}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                    {errors.confirmPassword && (
                      <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                        <AlertCircle className="h-4 w-4" />
                        {errors.confirmPassword}
                      </p>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Remember Me - Only in login mode */}
              {authMode === 'login' && (
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={formData.rememberMe || false}
                      onChange={(e) => handleInputChange('rememberMe', e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300 text-brand-primary focus:ring-brand-primary"
                    />
                    <span>{t('auth.rememberMe')}</span>
                  </label>
                </div>
              )}

              {/* GDPR Consent Section - Only show in register mode */}
              <AnimatePresence>
                {authMode === 'register' && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                    className="mt-6"
                  >
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <button
                        type="button"
                        onClick={() => setIsConsentExpanded(!isConsentExpanded)}
                        className="flex items-center gap-2 text-sm font-semibold text-gray-900 mb-3 hover:text-brand-primary transition-colors"
                      >
                        {isConsentExpanded ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                        {t('auth.consent.title')}
                      </button>

                      <AnimatePresence>
                        {isConsentExpanded && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.3 }}
                            className="text-xs text-gray-600 space-y-2 mb-4"
                          >
                            <p>{t('auth.consent.intro')}</p>
                            <p>{t('auth.consent.confidentiality')}</p>
                            <ul className="list-disc list-inside ml-2 space-y-1">
                              {(t('auth.consent.laws', { returnObjects: true }) as string[]).map((law: string, index: number) => (
                                <li key={index}>{law}</li>
                              ))}
                            </ul>
                            <p>{t('auth.consent.dataUsage')}</p>
                            <ul className="list-disc list-inside ml-2 space-y-1">
                              {(t('auth.consent.purposes', { returnObjects: true }) as string[]).map((purpose: string, index: number) => (
                                <li key={index}>{purpose}</li>
                              ))}
                            </ul>
                            <p>{t('auth.consent.dataProtection')}</p>
                            <p>{t('auth.consent.rights')}</p>
                          </motion.div>
                        )}
                      </AnimatePresence>

                      <div className="flex items-start gap-3">
                        <div className="flex items-center h-6">
                          <input
                            id="consent-checkbox"
                            type="checkbox"
                            checked={formData.consentToDataProcessing || false}
                            onChange={(e) => handleInputChange('consentToDataProcessing', e.target.checked)}
                            className={`w-5 h-5 sm:w-4 sm:h-4 rounded border-2 transition-colors ${errors.consentToDataProcessing
                              ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                              : 'border-gray-300 focus:border-brand-primary focus:ring-brand-primary/20'
                              } focus:outline-none focus:ring-2 text-brand-primary`}
                          />
                        </div>
                        <div className="flex-1">
                          <label htmlFor="consent-checkbox" className="text-sm text-gray-700 cursor-pointer">
                            <span className="font-medium">{t('auth.consent.checkbox')}</span>
                          </label>
                          {errors.consentToDataProcessing && (
                            <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                              <AlertCircle className="h-4 w-4" />
                              {errors.consentToDataProcessing}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-brand-primary text-white py-2 sm:py-3 px-4 rounded-lg font-semibold hover:bg-brand-primary/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 mt-4 sm:mt-6 text-sm sm:text-base touch-manipulation"
                style={{
                  WebkitTapHighlightColor: 'transparent',
                  touchAction: 'manipulation'
                }}
              >
                {isLoading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    {authMode === 'login' ? t('auth.consultant.form.signingIn') : t('auth.consultant.form.creating')}
                  </>
                ) : (
                  <>
                    {authMode === 'login' ? t('auth.consultant.form.loginButton') : t('auth.consultant.form.registerButton')}
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </button>
            </form>

            {/* Footer Links */}
            <div className="mt-6 text-center">
              {authMode === 'login' ? (
                <p className="text-sm text-gray-600">
                  {t('auth.consultant.form.noAccount')}{' '}
                  <button
                    onClick={toggleAuthMode}
                    className="text-emerald-600 hover:underline font-semibold"
                  >
                    {t('auth.consultant.form.createAccount')}
                  </button>
                </p>
              ) : (
                <p className="text-sm text-gray-600">
                  {t('auth.consultant.form.alreadyAccount')}{' '}
                  <button
                    onClick={toggleAuthMode}
                    className="text-emerald-600 hover:underline font-semibold"
                  >
                    {t('auth.consultant.form.signIn')}
                  </button>
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsultantAuthPage;